import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
} from "reactstrap";
import api from "../helpers/api";
import { formatCreatedDate } from "../constants/utils";
import CategoryModal from "../components/CategoryModal";
import PermissionGuard from "../components/Common/PermissionGuard";
import usePermission from "../Hooks/usePermission";
import { useTranslation } from "react-i18next";

const Categories = () => {
  const { t } = useTranslation();

  const currentUser = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = currentUser?.activeOrganizationId ?? null;
  const { hasPermission } = usePermission();

  const [categoryModal, setCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryDetailsModal, setCategoryDetailsModal] = useState(false);
  const [selectedCategoryForDetails, setSelectedCategoryForDetails] = useState(null);

  // Toggle category modal for create/edit
  function toggleCategoryModal(category = null) {
    setCategoryModal(!categoryModal);
    setSelectedCategory(category || {});
    setErrors({});
    setApiError("");
  }

  // Toggle category details modal
  const toggleCategoryDetailsModal = (category = null) => {
    setCategoryDetailsModal(!categoryDetailsModal);
    setSelectedCategoryForDetails(category);
  };

  const handleCategoryModalClose = () => {
    setCategoryModal(false);
    setSelectedCategory(null);
  };

  const handleCategoryUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError("");
    setErrors({});

    // Validation
    const newErrors = {};
    if (!selectedCategory?.name?.trim()) {
      newErrors.name = t('shared.category_name_required', 'Category name is required');
    } else if (selectedCategory.name.length > 100) {
      newErrors.name = t('shared.category_name_max_length', 'Category name must be 100 characters or less');
    }
    if (selectedCategory?.description && selectedCategory.description.length > 500) {
      newErrors.description = t('shared.description_max_length', 'Description must be 500 characters or less');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const categoryData = {
        name: selectedCategory.name.trim(),
        description: selectedCategory.description?.trim() || "",
        organizationId: organizationId,
      };

      if (selectedCategory?.id) {
        // Update existing category
        const updateData = {
          id: selectedCategory.id,
          ...categoryData
        };
        await api.put("organization-category/update", updateData);
      } else {
        // Create new category
        await api.post("organization-category/create", categoryData);
      }

      handleCategoryModalClose();
      reloadCategories(searchTerm);
    } catch (error) {
      console.error("Error during category submission:", error);
      setApiError(error?.response?.data?.message || t('shared.error_submission', 'An error occurred during submission'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedCategory?.id) {
        await api.delete(`organization-category/delete?id=${selectedCategory.id}`);
        reloadCategories(searchTerm);
        setDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      setApiError(t('shared.failed_to_delete_category', 'Failed to delete category'));
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  const reloadCategories = useCallback(async (search = "") => {
    if (!organizationId) return;

    setLoading(true);
    try {
      const { data } = await api.get(
        `organization-category/getAll?organizationId=${organizationId}&search=${search}`
      );
      setCategories(data.response || []);
    } catch (error) {
      console.error("Error reloading categories:", error);
      setApiError(t('shared.failed_to_load_categories', 'Failed to load categories'));
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  // Initial load
  useEffect(() => {
    reloadCategories(searchTerm);
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      reloadCategories(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, reloadCategories]);

  return (
    <div className="mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="category:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={() => {
                toggleCategoryModal();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                height={21}
                width={21}
              >
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
              </svg>
              {t('shared.add_category', 'Add Category')}
            </button>
          </PermissionGuard>
          <div
            className="ms-auto"
            style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}
          >
            <Input
              type="text"
              placeholder={t('shared.search_categories', 'Search Categories...')}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">{t('shared.categories', 'Categories')}</h3>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('shared.name', 'Name')}</th>
                  <th>{t('shared.created_date', 'Created Date')}</th>
                  <th>{t('shared.edit', 'Edit')}</th>
                  <th>{t('shared.delete', 'Delete')}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {categories?.map((category, ind) => (
                    <motion.tr
                      key={category.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>
                        <span
                          className="text-primary cursor-pointer hover-underline"
                          onClick={() => toggleCategoryDetailsModal(category)}
                          style={{ cursor: 'pointer' }}
                        >
                          {category.name}
                        </span>
                      </td>
                      <td>{formatCreatedDate(category.createdAt) || '-'}</td>
                      <td>
                        <PermissionGuard permission="category:update">
                          <span
                            className='text-warning'
                            role='button'
                            onClick={() => toggleCategoryModal(category)}
                          >
                            <FeatherIcon icon="edit" />
                          </span>
                        </PermissionGuard>
                      </td>
                      <td>
                        <PermissionGuard permission="category:delete">
                          <span
                            className='text-danger'
                            role='button'
                            onClick={() => {
                              setSelectedCategory(category);
                              setDeleteModal(true);
                            }}
                          >
                            <FeatherIcon icon="trash-2" />
                          </span>
                        </PermissionGuard>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="text-center py-3">
              <Spinner color="primary" />
            </div>
          )}

          {categories.length === 0 && !loading && (
            <div className="text-center py-5 text-muted">
              {searchTerm
                ? t('shared.no_categories_match', 'No categories match your search')
                : t('shared.no_categories_found', 'No categories found')}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Category Create/Edit Modal */}
      <Modal
        size="lg"
        isOpen={categoryModal}
        toggle={() => {
          toggleCategoryModal();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
              {selectedCategory?.id ? t('shared.edit_category', 'Edit Category') : t('shared.add_category', 'Add Category')}
            </h5>
            <button
              onClick={handleCategoryModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleCategoryUpdate}>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-name-input"
                  className="col-sm-3 col-form-label"
                >
                  {t('shared.name', 'Name')} <span className="text-danger">*</span>
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedCategory?.name || ""}
                    onChange={(e) =>
                      setSelectedCategory({
                        ...selectedCategory,
                        name: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    placeholder={t('shared.enter_category_name', 'Enter category name')}
                    id="horizontal-name-input"
                    maxLength={100}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-description-input"
                  className="col-sm-3 col-form-label"
                >
                  {t('shared.description', 'Description')}
                </label>
                <Col sm={9}>
                  <textarea
                    value={selectedCategory?.description || ""}
                    onChange={(e) =>
                      setSelectedCategory({
                        ...selectedCategory,
                        description: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.description ? "is-invalid" : ""
                    }`}
                    placeholder={t('shared.enter_category_description', 'Enter category description')}
                    id="horizontal-description-input"
                    rows={3}
                    maxLength={500}
                  />
                  {errors.description && (
                    <small className="text-danger">{errors.description}</small>
                  )}
                  <small className="text-muted">
                    {selectedCategory?.description?.length || 0}/500 {t('shared.characters', 'characters')}
                  </small>
                </Col>
              </Row>

              <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t('shared.processing', 'Processing...')
                    : selectedCategory?.id
                    ? t('shared.update', 'Update')
                    : t('shared.add', 'Add')}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">{t('shared.confirm_delete', 'Confirm Delete')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(false)}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body">
          <p>
            {t('shared.delete_category_confirmation', 'Are you sure you want to delete the category "{{name}}"? This action cannot be undone.', { name: selectedCategory?.name })}
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModal(false)}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
          >
            {t('shared.delete', 'Delete')}
          </button>
        </div>
      </Modal>

      {/* Category Details Modal */}
      <CategoryModal
        isOpen={categoryDetailsModal}
        toggle={toggleCategoryDetailsModal}
        categoryData={selectedCategoryForDetails}
        loading={false}
        onEditCategory={hasPermission('category:update') ? (category) => {
          toggleCategoryDetailsModal();
          toggleCategoryModal(category);
        } : null}
      />
    </div>
  );
};

export default Categories;
